import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { X, Loader2, Clock, Trash2 } from "lucide-react";

const inputCls = "w-full rounded-lg border border-[var(--njas-border)] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-njas-sage focus:border-njas-sage";

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Admin-only modal to schedule / edit / delete a shift.
 *
 * Props:
 *  - mode: "create" | "edit"
 *  - shift: existing shift (edit mode)
 *  - prefill: { user_id?, client_id?, scheduled_start?, scheduled_end? }
 *  - workers, clients: prefetched lists (if omitted, fetched here)
 *  - lockClient: hide the client dropdown (used from within a client calendar)
 *  - onClose(), onSaved(): callbacks
 */
export default function ScheduleShiftModal({
  mode = "create", shift, prefill = {}, workers: workersProp, clients: clientsProp,
  lockClient = false, onClose, onSaved,
}) {
  const editing = mode === "edit";
  const isImmutable = editing && shift?.status !== "scheduled";

  const [workers, setWorkers] = useState(workersProp || []);
  const [clients, setClients] = useState(clientsProp || []);
  const [userId, setUserId] = useState(shift?.user_id || prefill.user_id || "");
  const [clientId, setClientId] = useState(shift?.client_id || prefill.client_id || "");
  const [start, setStart] = useState(shift ? toDatetimeLocal(shift.scheduled_start) : (prefill.scheduled_start || ""));
  const [end, setEnd] = useState(shift ? toDatetimeLocal(shift.scheduled_end) : (prefill.scheduled_end || ""));
  const [notes, setNotes] = useState(shift?.notes || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!workersProp) {
      api.get("/staff/list").then((r) => setWorkers((r.data || []).filter((u) => ["peer_worker", "staff"].includes(u.role)))).catch(() => {});
    }
    if (!clientsProp && !lockClient) {
      api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    }
  }, [workersProp, clientsProp, lockClient]);

  const save = async () => {
    if (!userId) { toast.error("Please select a worker"); return; }
    if (!start || !end) { toast.error("Please set start and end times"); return; }
    if (new Date(end) <= new Date(start)) { toast.error("End must be after start"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/shifts/${shift.id}`, {
          user_id: userId, client_id: clientId || "", scheduled_start: start, scheduled_end: end, notes,
        });
        toast.success("Shift updated");
      } else {
        await api.post("/shifts/schedule", {
          user_id: userId, client_id: clientId || null, scheduled_start: start, scheduled_end: end, notes,
        });
        toast.success("Shift scheduled");
      }
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const del = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this scheduled shift?")) return;
    setBusy(true);
    try {
      await api.delete(`/shifts/${shift.id}`);
      toast.success("Shift deleted");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 py-10 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="schedule-shift-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="eyebrow text-njas-sage-hover text-[10px]">{editing ? "Edit shift" : "New shift"}</div>
            <h3 className="font-serif text-2xl text-njas-navy">Schedule shift</h3>
          </div>
          <button onClick={onClose} className="p-1 text-njas-muted hover:text-njas-navy"><X className="w-5 h-5" /></button>
        </div>

        {isImmutable && (
          <div className="text-xs bg-amber-50 text-amber-800 rounded-lg p-2 mb-3">
            This shift is already {shift?.status}. Only unstarted shifts can be edited.
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wider text-njas-muted mb-1">Worker</label>
            <select value={userId} onChange={(e) => setUserId(e.target.value)} className={inputCls} disabled={isImmutable} data-testid="schedule-worker-select">
              <option value="">Select worker…</option>
              {workers.map((w) => <option key={w.id} value={w.id}>{w.name} · {(w.role || "").replace("_", " ")}</option>)}
            </select>
          </div>
          {!lockClient && (
            <div>
              <label className="block text-xs uppercase tracking-wider text-njas-muted mb-1">Client (optional)</label>
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputCls} disabled={isImmutable} data-testid="schedule-client-select">
                <option value="">General (no client)</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-njas-muted mb-1">Start</label>
              <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} disabled={isImmutable} data-testid="schedule-start" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-njas-muted mb-1">End</label>
              <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className={inputCls} disabled={isImmutable} data-testid="schedule-end" />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-njas-muted mb-1">Notes</label>
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Court accompaniment, phone check-in…" className={inputCls} disabled={isImmutable} data-testid="schedule-notes" />
          </div>
        </div>

        <div className="flex justify-between items-center mt-5">
          {editing ? (
            <button onClick={del} disabled={busy} className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700" data-testid="schedule-delete-btn">
              <Trash2 className="w-4 h-4" /> Delete
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-full border border-[var(--njas-border)] bg-white px-4 py-2 text-sm text-njas-navy hover:bg-njas-bg-alt">Cancel</button>
            <button onClick={save} disabled={busy || isImmutable} data-testid="schedule-save-btn" className="inline-flex items-center gap-2 rounded-full bg-njas-navy text-white px-5 py-2 text-sm hover:bg-njas-navy-hover disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />} {editing ? "Save" : "Schedule"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
